import * as colors from '@giphy/js-brand'
import { giphyDarkestGrey, giphyRed } from '@giphy/js-brand'
import { each } from 'lodash'
import { useContext, useRef, useState } from 'react'
import { createUser } from 'shared/api'
import Captcha from 'shared/components/captcha'
import { FlashMessageContext } from 'shared/components/flash-message/flash-message'
import AppleButton from 'shared/components/form/apple-button'
import { TermsOfService } from 'shared/components/form/copy'
import FacebookButton from 'shared/components/form/facebook-button'
import JoinFields, { FIELDS } from 'shared/components/form/fields'
import { Content, CTAButton as BrandCTAButton, FormContainer } from 'shared/components/form/form-components'
import SnakeLoader from 'shared/components/snake-loader'
import MessageContext from 'shared/contexts/message'
import { assertIsDefined } from 'shared/types/asserts'
import { GOOGLE_RECAPTCHA_KEY } from 'shared/variables'
import styled from 'styled-components'
import Checkbox from '../../../shared/components/checkbox'
import { mobile } from '../../../shared/util/media-query'

const sectionMargin = 7

const FormContent = styled(Content)<{ width?: number }>`
    width: ${(props) => props.width}px;
`

const AppleWithMargin = styled(AppleButton)`
    margin-top: 8px;
    margin-bottom: ${sectionMargin}px;
`

const CaptchaWithMargin = styled(Captcha)`
    margin-bottom: ${sectionMargin}px;
`

const LoaderContainer = styled.div`
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
`

const CTAButton = styled(BrandCTAButton)`
    width: 100%;
    position: relative;
    background: linear-gradient(-135deg, #6157ff 0%, #9933ff 100%);
    opacity: ${(props) => (props.disabled ? '60' : '100')}% !important;
    font-size: 15px;
    border-radius: 3px;
    cursor: pointer;
    margin-bottom: 0;
`

const SignUpRecovery = styled.p`
    text-align: center;
    margin-top: -12px;
    margin-bottom: 4px;
    font-size: 14px !important;

    ${mobile.css`
    margin-top: -4px;
    margin-bottom: 12px;
  `}
`

const SignUpRecoveryLink = styled.button`
    background: none;
    border: none;
    display: inline-block;
    padding: 0;
    margin-left: 10px;
    font-size: 14px !important;
    color: white;
    font-weight: bold;
    font-family: inherit;
`

const TermsAgreement = styled.div`
    display: flex;
    padding: 10px 2px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    * {
        text-align: left;
    }
`

const TermsCheckbox = styled(Checkbox)<{ active?: boolean }>`
    width: 30px;
    box-sizing: border-box;
    height: 24px;
    border: 4px solid ${(props) => (props.active ? giphyRed : giphyDarkestGrey)};
    border-radius: 4px;
    background-color: ${colors.giphyBlack} ${mobile.css`
        width: 74px;
        margin-top: 5px;
    `};
`

type Props = {
    width?: number
    onJoinSuccess?: (object) => void
    onFinishSignUpClick?: () => void
}
const SimpleMessage = styled.div`
    text-align: left;
    flex: 1;
    padding: 20px;
`
const termsMessage =
    'Please agree to our terms of service and acknowledge that you are at least 13 years of age by checking the box below'
const JoinForm = ({ width, onJoinSuccess, onFinishSignUpClick }: Props) => {
    const { sendMessage } = useContext(MessageContext)
    const { showMessage, hideMessage, messageQueue } = useContext(FlashMessageContext)
    const formRef = useRef<HTMLFormElement | null>(null)
    const [isFormValid, setIsFormValid] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [isCaptchaSolved, setIsCaptchaSolved] = useState<boolean>(false)
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false)

    const areFieldsOkay = () => isFormValid && isCaptchaSolved
    const onValidChange = (valid) => {
        setIsFormValid(valid)
    }
    const onCaptchaChange = (responseToken) => {
        setIsCaptchaSolved(!!responseToken)
    }
    const onCaptchaError = () => {
        onCaptchaChange(false)
    }
    const showTermsMessage = () => {
        showMessage({
            gradientColors: ['#e646b6', '#ff6666'],
            messageType: 'failure',
            message: termsMessage,
            messageComponent: SimpleMessage,
        })
    }
    const submit = async (event) => {
        event.stopPropagation()
        event.preventDefault()
        if (areFieldsOkay() && !hasAcceptedTerms) {
            showTermsMessage()
        } else {
            setLoading(true)
            if (areFieldsOkay()) {
                assertIsDefined(formRef.current)
                const formData = new FormData(formRef.current)
                try {
                    const response = await createUser({
                        username: formData.get('username'),
                        email: formData.get('email'),
                        password: formData.get('password'),
                        recaptcha_token: formData.get('g-recaptcha-response'),
                        platform: 'web',
                    })

                    if (onJoinSuccess) {
                        onJoinSuccess(response)
                    }
                } catch (err) {
                    if (err.details && err.details.code) {
                        showMessage({ slug: err.details.code })
                    } else if (err.details) {
                        each(err.details, (values) => {
                            each(values, (value) => {
                                sendMessage(value, 'error')
                            })
                        })
                    }
                    setLoading(false)
                    // @ts-ignore
                    // this is sketcy, but the recaptcha we're using doesn't provide a ref we can access to do this
                    window.grecaptcha.reset()
                    setIsCaptchaSolved(false)
                }
            }
        }
    }
    return (
        <FormContainer>
            <form onSubmit={submit} ref={formRef}>
                <FormContent width={width}>
                    <SignUpRecovery>
                        Already started creating an account?{' '}
                        <SignUpRecoveryLink onClick={onFinishSignUpClick} type="button">
                            Finish Sign Up
                        </SignUpRecoveryLink>
                    </SignUpRecovery>
                    <JoinFields
                        onValidChange={onValidChange}
                        showFields={[FIELDS.email, FIELDS.username, FIELDS.password, FIELDS.passwordConfirmation]}
                        borderRadius={3}
                    />
                    <CaptchaWithMargin
                        theme="dark"
                        size="normal"
                        sitekey={GOOGLE_RECAPTCHA_KEY}
                        onChange={onCaptchaChange}
                        onErrored={onCaptchaError}
                    />
                    <CTAButton
                        color={!areFieldsOkay() || loading ? colors.giphyDarkGrey : colors.primaryCTA}
                        disabled={!areFieldsOkay() || loading}
                    >
                        Sign Up
                        {loading && (
                            <LoaderContainer>
                                <SnakeLoader />
                            </LoaderContainer>
                        )}
                    </CTAButton>
                    <AppleWithMargin
                        label="Sign Up with Apple"
                        onClick={() => {
                            if (!hasAcceptedTerms) {
                                showTermsMessage()
                                return
                            }
                            location.href = `/login/apple/${location.search}`
                        }}
                    />
                    <FacebookButton
                        label="Sign Up with Facebook"
                        onClick={() => {
                            if (!hasAcceptedTerms) {
                                showTermsMessage()
                                return
                            }
                            location.href = `/login/facebook/${location.search}`
                        }}
                    />
                    <TermsAgreement>
                        <TermsCheckbox
                            active={messageQueue?.[0]?.message === termsMessage}
                            isChecked={hasAcceptedTerms}
                            onChange={(value: boolean) => {
                                setHasAcceptedTerms(value)
                                hideMessage()
                            }}
                        />
                        <TermsOfService />
                    </TermsAgreement>
                </FormContent>
            </form>
        </FormContainer>
    )
}

export default JoinForm
